import { css } from '@emotion/react';
import { useProgressBar } from '@react-aria/progress';
import type { ComponentPropsWithoutRef } from 'react';
import { ProgressProvider } from './context';
import type { ProgressAppearance, ProgressSize } from './types';

export interface ProgressProps extends ComponentPropsWithoutRef<'div'> {
  /** The size of the spinner. */
  size?: ProgressSize;
  /** Set the state of the spinner to indeterminate. */
  isIndeterminate?: boolean;
  /** The current value of the Progress (controlled). */
  value?: number;
  /** The minimum value of the Progress. */
  minValue?: number;
  /** The maximum value of the Progress. */
  maxValue?: number;
  /** The appearance of the Progress. */
  appearance?: ProgressAppearance;
}

const baseStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

export const Progress = ({
  size = 'medium',
  appearance = 'primary',
  isIndeterminate = false,
  value = 0,
  minValue = 0,
  maxValue = 100,
  children,
  ...restProps
}: ProgressProps) => {
  const { progressBarProps, labelProps } = useProgressBar({
    // Hardcoded to bypass react-aria requirement for a label. We provide our own label.
    label: 'LABEL',
    value,
    minValue,
    maxValue,
    isIndeterminate,
  });

  const percentage = (value - minValue) / (maxValue - minValue);

  const contextValue = {
    progressBarProps,
    labelProps,
    size,
    isIndeterminate,
    percentage,
    appearance,
  };

  return (
    <div css={baseStyles} {...restProps}>
      <ProgressProvider value={contextValue}>{children}</ProgressProvider>
    </div>
  );
};
